import Loader from 'components/ui/LoadingIndicator';
import Link from 'components/ui/AppLink';
import ContainerBox from 'components/ui/ContainerBox';
import {
    Accordion as ExpansionPanel,
    AccordionDetails as ExpansionPanelDetails,
    AccordionSummary as ExpansionPanelSummary,
    Box,
    Breadcrumbs,
    Button,
    Divider,
    Typography,
    Typography as BreadcrumbsText,
} from '@mui/material';
import { Option, Select, TextField } from 'components/ui/FormControls';
import React, { useEffect, useState } from 'react';

import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { useUser } from '../../providers/UserProvider';
import {
    prepareStaticDataList,
    categoriesToList,
} from '../../../utils/list-utils';
import { formatPublishDate } from '../../../utils/date.utils';
import {
    getContributorRoleUsers,
    getUsersWithNames,
} from '../../../utils/filtering.utils';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { translateErrorMessage } from '../../../shared/error-handling';
import { Formik, Form, useField, FieldAttributes } from 'formik';
import * as Yup from 'yup';
import {
    categoryFieldsFragment,
    categoriesAsTreePartial,
} from '../../../gql/categories';
import gql from 'graphql-tag';
import {
    FormSnackBarType,
    generateSnackBarFormMessage,
} from '../../../utils/form.utils';
import { ManageTemplateInstance } from '../../ui/ManageTemplate';
import { useNotification } from '../../providers/NotificationProvider';
import TemplateEditor from '../../ui/admin/TemplateEditor';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { USER_PUBLISH_CHANNELS } from '../../../gql/users';
import { topicsOrCategories } from '../../../utils/utils';
import { sanitizeRouteId } from '../../../utils/route-params.utils';

import { useHistory, useParams } from 'react-router-dom';
import {
    AlertMessage,
    CategoryItemNode,
    IdAndName,
    Template,
    Desk,
    User,
    Channel,
    Publisher,
} from '../../../shared/types';
import { AlertBox } from 'components/ui/Feedback';

const returnListUrl = baseUrls.publisher.templates.list;

const ADD_TEMPLATE = gql`
    mutation addTemplate($item: TemplateInputAdd) {
        addTemplate(item: $item) {
            _id
        }
    }
`;

const EDIT_TEMPLATE = gql`
    mutation editTemplate($item: TemplateInputEdit) {
        editTemplate(item: $item) {
            _id
        }
    }
`;

const DELETE_TEMPLATE = gql`
    mutation DeleteTemplate($id: ID!) {
        deleteTemplate(id: $id) {
            _id
        }
    }
`;

const GET_TEMPLATE = gql`
query templatePage($id: ID, $withTemplate: Boolean!) {
	document: findTemplateById(id: $id) @include(if: $withTemplate) {
		_id
		author
		title
		html
		publishedBy { _id name type }
		contributors { _id name }
		categories { _id name }
		tags { _id name }
		created
		version
		lastEdited
		premiumChannel
	}
	tags { _id name }
	desks { _id name }
	users: contributors { _id scopes name }
	${categoriesAsTreePartial}
}
${categoryFieldsFragment}`;

const MAGIC_SPLIT = '-_-';

const toIds = (arr?: Option[]) => arr?.map((o) => o.value) ?? [];
const toOption = (x?: Partial<IdAndName>): Option | undefined =>
    x && { value: x._id || '', label: x.name || '' };
const toOptions = (arr?: Partial<IdAndName>[]): Option[] =>
    (arr?.map(toOption) as Option[]) || [];
const toPublisherOption = (publishedBy: Publisher): Option => ({
    value: [publishedBy.type, publishedBy._id].join(MAGIC_SPLIT),
    label: publishedBy.name,
});
const publisherOptionToPublisherInput = (value: string) => {
    const [type, _id] = value.split(MAGIC_SPLIT);
    return { type, _id };
};
const channelToPublisher = (channel: Channel): Publisher => ({
    ...channel.owner,
    type: channel.ownerType,
});

export const TemplateAddEdit = () => {
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const { showMessage } = useNotification();
    const { userDetails } = useUser();
    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [pageMessage, setPageMessage] = useState<AlertMessage>({
        style: 'info',
        message: null,
    });

    const { data, loading } = useQuery<{
        document?: Template;
        categoriesAsTree: CategoryItemNode[];
        desks: Desk[];
        users: User[];
        tags: IdAndName[];
    }>(GET_TEMPLATE, {
        variables: { id: _id, withTemplate: !!_id },
        fetchPolicy: 'network-only',
    });

    const [getChannels, { loading: channelsLoading, data: channelsData }] =
        useLazyQuery(USER_PUBLISH_CHANNELS, {
            fetchPolicy: 'network-only',
        });

    useEffect(() => {
        getChannels();
    }, [getChannels]);

    const [addDbAction] = useMutation(ADD_TEMPLATE);
    const [editDbAction] = useMutation(EDIT_TEMPLATE);
    const [deleteDbAction] = useMutation(DELETE_TEMPLATE);

    const history = useHistory();
    const goBack = () => history.push(returnListUrl);

    if (loading || !data || channelsLoading || !channelsData) return <Loader />;

    const handleDelete = (id?: string, title?: string) => () => {
        if (!id) return;
        try {
            deleteDbAction({ variables: { id } });
            if (title) {
                showMessage(
                    generateSnackBarFormMessage(
                        ManageTemplateInstance.Template,
                        title,
                        FormSnackBarType.Deleted
                    )
                );
            }
            goBack();
        } catch (error) {
            setPageMessage({
                style: 'error',
                message: translateErrorMessage(error.message),
            });
        }
    };

    const findUsedChannel = (channelId?: string): Channel | undefined =>
        channelsData?.me?.channelsToPublish?.find(
            (c: Channel) => c._id.toString() === channelId
        );

    const template = data?.document;
    const editMode = !!template;
    const currentChannel = findUsedChannel(
        template?.premiumChannel?.toString()
    );

    return (
        <Formik
            initialValues={{
                title: template?.title || '',
                html: template?.html || '',
                publishedBy: template?.publishedBy
                    ? toPublisherOption(template?.publishedBy)
                    : currentChannel
                      ? toPublisherOption(channelToPublisher(currentChannel))
                      : null,
                contributors:
                    toOptions(
                        getUsersWithNames(template?.contributors as User[])
                    ) || [],
                categories: toOptions(template?.categories) || [],
                tags: toOptions(template?.tags) || [],
                premiumChannel: toOption(currentChannel),
            }}
            validationSchema={Yup.object({
                title: Yup.string().required('This field is required.'),
                premiumChannel: Yup.object()
                    .nullable()
                    .required('This field is required.'),
            })}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    setSubmitting(true);
                    const saveFn = editMode ? editDbAction : addDbAction;
                    await saveFn({
                        variables: {
                            item: {
                                _id,
                                publishedBy: values.publishedBy?.value
                                    ? publisherOptionToPublisherInput(
                                          `${values.publishedBy.value}`
                                      )
                                    : undefined,
                                contributors: toIds(values.contributors),
                                author: userDetails?.id,
                                title: values.title || undefined,
                                html: values.html || '',
                                categories: toIds(values.categories),
                                tags: toIds(values.tags),
                                premiumChannel:
                                    values.premiumChannel?.value || null,
                                version: data?.document?.version,
                            },
                        },
                    });
                    showMessage(
                        generateSnackBarFormMessage(
                            ManageTemplateInstance.Template,
                            values.title,
                            editMode
                                ? FormSnackBarType.Updated
                                : FormSnackBarType.Added
                        )
                    );
                    goBack();
                } catch (error) {
                    setSubmitting(false);
                    setPageMessage({
                        style: 'error',
                        message: translateErrorMessage(error.message),
                    });
                }
            }}
        >
            {({ values, setFieldValue, errors }) => (
                <Box component={Form} sx={{ height: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            height: '100%',
                            pl: 2,
                        }}
                    >
                        <ContainerBox sx={{ px: 0, mt: 2 }}>
                            <Box sx={{ maxWidth: 1200, width: '100%' }}>
                                <Breadcrumbs>
                                    <Link to={returnListUrl}>Templates</Link>
                                    <BreadcrumbsText>
                                        {editMode
                                            ? 'Edit template'
                                            : 'New template'}
                                    </BreadcrumbsText>
                                </Breadcrumbs>
                                <Typography sx={{ mt: 4, mb: 1 }} variant="h5">
                                    {!template?.title
                                        ? 'Article template'
                                        : `${editMode && 'Edit template'} ${template?.title}`}
                                </Typography>
                                <Typography variant="body1">
                                    Add some placeholder content to define the
                                    base structure of this article.
                                </Typography>
                                <Box sx={{ my: 2 }}>
                                    <AlertBox
                                        id="article-add-edit-alert"
                                        visible={!!pageMessage.message}
                                        children={pageMessage.message}
                                        styleName={pageMessage.style}
                                        onClose={() =>
                                            setPageMessage({
                                                style: 'info',
                                                message: null,
                                            })
                                        }
                                    />
                                </Box>
                                <Box sx={{ mt: 2, maxWidth: 820 }}>
                                    {
                                        // Froala bug. Can't send multiple updates in quick succession.
                                        // See https://github.com/froala/react-froala-wysiwyg/issues/221#issuecomment-605609436
                                    }
                                    {(values.html !== undefined ||
                                        !editMode) && (
                                        <TemplateEditor
                                            initialHtml={values.html ?? ' '}
                                            errorMessage={errors.html}
                                            onChange={(html) =>
                                                setFieldValue('html', html)
                                            }
                                        />
                                    )}
                                </Box>
                            </Box>
                        </ContainerBox>
                        <Box
                            sx={{
                                bgcolor: 'background.paper',
                                borderLeft: 1,
                                borderColor: 'divider',
                                ml: 2,
                                minWidth: 368,
                                width: 368,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Box
                                    sx={{ p: 2, '&>button': { width: '100%' } }}
                                >
                                    <Button
                                        id="save-template-button"
                                        variant="contained"
                                        size="medium"
                                        type="submit"
                                    >
                                        {!editMode
                                            ? 'Save template'
                                            : 'Update template'}
                                    </Button>
                                </Box>
                                {editMode && (
                                    <>
                                        <Divider />
                                        <Box sx={{ m: 2 }}>
                                            <Box sx={{ mb: 1 }}>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    Last edit:
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    sx={{
                                                        ml: 1,
                                                        fontWeight:
                                                            'fontWeightBold',
                                                    }}
                                                >
                                                    {template &&
                                                        formatPublishDate(
                                                            new Date(
                                                                template.lastEdited
                                                            ),
                                                            true
                                                        )}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                >
                                                    Created:
                                                </Typography>
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    sx={{
                                                        ml: 1,
                                                        fontWeight:
                                                            'fontWeightBold',
                                                    }}
                                                >
                                                    {template &&
                                                        formatPublishDate(
                                                            new Date(
                                                                template.created
                                                            ),
                                                            true
                                                        )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider />
                                        <Box sx={{ m: 2 }}>
                                            <Button
                                                id="delete-template-button"
                                                sx={{ mb: 1 }}
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() =>
                                                    setDeleteDialogOpen(true)
                                                }
                                            >
                                                Delete this template
                                            </Button>
                                            <Typography
                                                variant="caption"
                                                sx={{ display: 'block' }}
                                            >
                                                Deleting a template won't affect
                                                existing articles.
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                                <Divider />
                            </Box>
                            <Box>
                                <SidebarPanel
                                    label="Template details"
                                    templateDetails
                                >
                                    <TextInput
                                        id="template-name-input"
                                        label="Template name"
                                        name="title"
                                        placeholder="Daily Report {{Today}}"
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', mb: 1 }}
                                    >
                                        The template name is used to generate
                                        the <strong>article title</strong> every
                                        time new content is created.
                                    </Typography>
                                    <Typography variant="caption">
                                        Use the <strong>{'{{Today}}'}</strong>{' '}
                                        placeholder to have the publish date
                                        generated automatically for you.
                                    </Typography>
                                </SidebarPanel>
                                <SidebarPanel label="Article details">
                                    <SelectInput
                                        id="premium-channel-select"
                                        label="Channel"
                                        name="premiumChannel"
                                        options={prepareStaticDataList(
                                            channelsData?.me?.channelsToPublish
                                        )}
                                        onChange={(value) => {
                                            if (!value) {
                                                setFieldValue(
                                                    'publishedBy',
                                                    null
                                                );
                                                return;
                                            }
                                            const channel = findUsedChannel(
                                                `${value.value}`
                                            );
                                            setFieldValue(
                                                'publishedBy',
                                                channel
                                                    ? toPublisherOption(
                                                          channelToPublisher(
                                                              channel
                                                          )
                                                      )
                                                    : null
                                            );
                                        }}
                                    />
                                    <SelectInput
                                        id="contributor-select"
                                        multi
                                        label="Contributors"
                                        name="contributors"
                                        options={prepareStaticDataList(
                                            getContributorRoleUsers(
                                                getUsersWithNames(data?.users)
                                            )
                                        )}
                                        required={false}
                                    />
                                </SidebarPanel>
                                <SidebarPanel label="Taxonomies">
                                    <CategoryInput
                                        treeCategories={data?.categoriesAsTree}
                                        required={false}
                                    />
                                    <SelectInput
                                        id="tag-select"
                                        multi
                                        label="Tags"
                                        name="tags"
                                        options={prepareStaticDataList(
                                            data?.tags
                                        )}
                                        required={false}
                                    />
                                </SidebarPanel>
                            </Box>
                            <ManageTemplateDialog
                                onDelete={handleDelete(
                                    template?._id,
                                    template?.title
                                )}
                                submitBtnColorType="error"
                                dialogMessage={
                                    <>
                                        You are about to delete the template{' '}
                                        <strong> {values.title}</strong>. This
                                        action cannot be undone, so you must be
                                        sure and confirm.
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{ display: 'block', mt: 1 }}
                                        >
                                            Please rest assured that all the
                                            articles created from this template
                                            won't be affected nor deleted.
                                        </Typography>
                                    </>
                                }
                                title={'Delete this template?'}
                                submitBtnText={'Yes, delete'}
                                dialogOpenState={isDeleteDialogOpen}
                                closeDialog={() => setDeleteDialogOpen(false)}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
        </Formik>
    );
};

interface SidebarPanelProps {
    label: string;
    children: React.ReactNode;
    templateDetails?: boolean;
}

const SidebarPanel: React.FC<SidebarPanelProps> = ({
    label,
    children,
    templateDetails = false,
}) => {
    return (
        <ExpansionPanel sx={{ overflow: 'visible' }} defaultExpanded={true}>
            <ExpansionPanelSummary>{label}</ExpansionPanelSummary>
            <ExpansionPanelDetails
                sx={{
                    bgcolor: 'background.paper',
                    display: 'block',
                    '&>div:not(:last-child)': {
                        mb: templateDetails ? 1 : 2,
                    },
                }}
            >
                {children}
            </ExpansionPanelDetails>
        </ExpansionPanel>
    );
};

const TextInput: React.FC<
    FieldAttributes<{
        label: string;
        name: string;
        placeholder: string;
        id: string;
    }>
> = ({ label, name, placeholder, id }) => {
    const [{ value, ...field }, meta] = useField({ name });
    return (
        <TextField
            {...field}
            value={value}
            label={label}
            error={meta.touched && !!meta.error}
            errorText={meta.error}
            placeholder={placeholder}
            id={id}
        />
    );
};

interface SelectInputProps {
    label: string;
    name: string;
    options: Option[];
    multi?: boolean;
    hideSelectedOptions?: boolean;
    id?: string;
    required?: boolean;
    onChange?: (value: Option) => void;
    disabled?: boolean;
}

const SelectInput: React.FC<SelectInputProps> = (props) => {
    const [{ value, ...field }, meta, helpers] = useField({
        multiple: true,
        name: props.name,
    });
    return (
        <Select
            {...field}
            value={value}
            label={props.label}
            error={meta.touched && !!meta.error}
            errorText={meta.error}
            options={props.options}
            onChange={(option) => {
                helpers.setValue(option);
                props.onChange?.(option);
            }}
            onBlur={() => helpers.setTouched(true)}
            multi={props.multi}
            disabled={props.disabled}
            menuPlacement="auto"
            hideSelectedOptions={props.hideSelectedOptions ?? true}
            placeholder="Select from the list..."
            id={props.id}
            required={props.required}
        />
    );
};

const CategoryInput: React.FC<any> = ({ treeCategories = [], required }) => {
    const [{ value }] = useField({ name: 'categories' });
    const categoryOptions = categoriesToList<Option>(treeCategories, (cat) => ({
        label: cat.name,
        value: cat._id,
        disabled: !!value?.find((v: any) => v.value === cat._id),
        indentation: cat.level,
    }));
    return (
        <SelectInput
            id="category-select"
            multi
            hideSelectedOptions={false}
            label={topicsOrCategories(true, true)}
            name="categories"
            options={categoryOptions}
            required={required}
        />
    );
};

export default TemplateAddEdit;
